import { getAnalyticsEvents } from './analyticsService';
import { getCollection } from './firestoreService';
import { differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns';

export interface OnboardingFunnelStep {
  step: string;
  count: number;
  conversionRate: number;
  dropOff: number;
}

export interface ActivationMetrics {
  timeToFirstTopic: number; // in minutes
  timeToFirstSession: number; // in minutes
  onboardingCompletionRate: number;
}

export interface FirstExperience {
  firstTopicCategory: string;
  firstSessionDuration: number;
  topicsAddedInFirstSession: number;
  assistantUsedInFirstSession: boolean;
}

export interface OnboardingAnalytics {
  funnel: OnboardingFunnelStep[];
  activationMetrics: ActivationMetrics;
  firstExperiences: FirstExperience[];
  dropOffPoints: Array<{ step: string; dropOffRate: number; count: number }>;
}

export const getOnboardingAnalytics = async (
  days: number = 30
): Promise<OnboardingAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days * 2); // Get more events for user journey tracking
    const users = await getCollection('users', undefined, 'createdAt', 'desc', 1000);

    // Step 1: Account creation
    const accountCreations = events.filter((e) => e.eventName === 'user_registration' || e.eventName.includes('registration'));
    const accountCreationCount = new Set(accountCreations.map((e) => e.userId)).size;

    // Step 2: Onboarding start
    const onboardingStarts = events.filter((e) => e.eventName === 'onboarding_started' || e.eventName === 'onboarding_completed' || e.eventName === 'tutorial_completed');
    const onboardingStartCount = new Set(onboardingStarts.map((e) => e.userId)).size;

    // Step 3: First topic selection
    const firstTopicSelections = events.filter((e) => e.eventName === 'topic_selected' || e.eventName === 'assistant_topic_added');
    const topicSelectionUsers = new Map<string, Date>();
    firstTopicSelections.forEach((e) => {
      if (!topicSelectionUsers.has(e.userId)) {
        topicSelectionUsers.set(e.userId, e.timestamp);
      }
    });
    const firstTopicCount = topicSelectionUsers.size;

    // Step 4: First learning session
    const firstLearningSessions = events.filter((e) => e.eventName === 'learning_path_started');
    const learningSessionUsers = new Map<string, Date>();
    firstLearningSessions.forEach((e) => {
      if (!learningSessionUsers.has(e.userId)) {
        learningSessionUsers.set(e.userId, e.timestamp);
      }
    });
    const firstSessionCount = learningSessionUsers.size;

    // Step 5: Subscription (for paywall)
    const subscriptions = events.filter((e) => e.eventName === 'subscription_started' || e.eventName === 'subscription_completed');
    const subscriptionCount = new Set(subscriptions.map((e) => e.userId)).size;

    // Build funnel
    const funnel: OnboardingFunnelStep[] = [
      {
        step: 'Account Created',
        count: accountCreationCount || users.length,
        conversionRate: 100,
        dropOff: 0,
      },
      {
        step: 'Onboarding Started',
        count: onboardingStartCount,
        conversionRate: accountCreationCount > 0 ? (onboardingStartCount / accountCreationCount) * 100 : 0,
        dropOff: accountCreationCount - onboardingStartCount,
      },
      {
        step: 'First Topic Selected',
        count: firstTopicCount,
        conversionRate: accountCreationCount > 0 ? (firstTopicCount / accountCreationCount) * 100 : 0,
        dropOff: onboardingStartCount - firstTopicCount,
      },
      {
        step: 'First Learning Session',
        count: firstSessionCount,
        conversionRate: accountCreationCount > 0 ? (firstSessionCount / accountCreationCount) * 100 : 0,
        dropOff: firstTopicCount - firstSessionCount,
      },
      {
        step: 'Subscription Started',
        count: subscriptionCount,
        conversionRate: accountCreationCount > 0 ? (subscriptionCount / accountCreationCount) * 100 : 0,
        dropOff: firstSessionCount - subscriptionCount,
      },
    ];

    // Calculate drop-off points
    const dropOffPoints = funnel.slice(1).map((step, index) => ({
      step: step.step,
      dropOffRate: step.dropOff,
      count: step.dropOff,
    }));

    // Calculate activation metrics
    const timeToFirstTopicArray: number[] = [];
    const timeToFirstSessionArray: number[] = [];

    users.slice(0, 100).forEach((user: any) => {
      const signupDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      const userId = user.id;

      const firstTopicTime = topicSelectionUsers.get(userId);
      if (firstTopicTime) {
        const minutes = differenceInMinutes(firstTopicTime, signupDate);
        timeToFirstTopicArray.push(minutes);
      }

      const firstSessionTime = learningSessionUsers.get(userId);
      if (firstSessionTime) {
        const minutes = differenceInMinutes(firstSessionTime, signupDate);
        timeToFirstSessionArray.push(minutes);
      }
    });

    const timeToFirstTopic = timeToFirstTopicArray.length > 0
      ? timeToFirstTopicArray.reduce((a, b) => a + b, 0) / timeToFirstTopicArray.length
      : 0;

    const timeToFirstSession = timeToFirstSessionArray.length > 0
      ? timeToFirstSessionArray.reduce((a, b) => a + b, 0) / timeToFirstSessionArray.length
      : 0;

    const onboardingCompleted = events.filter((e) => e.eventName === 'onboarding_completed').length;
    const onboardingCompletionRate = accountCreationCount > 0 ? (onboardingCompleted / accountCreationCount) * 100 : 0;

    // First experience analytics
    const firstExperiences: FirstExperience[] = [];
    const processedUsers = new Set<string>();

    users.slice(0, 50).forEach((user: any) => {
      if (processedUsers.has(user.id)) return;
      processedUsers.add(user.id);

      const userEvents = events.filter((e) => e.userId === user.id).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      const firstTopicEvent = userEvents.find((e) => e.eventName === 'topic_selected' || e.eventName === 'assistant_topic_added');
      const firstSessionEvent = userEvents.find((e) => e.eventName === 'learning_path_started');
      const assistantEvents = userEvents.filter((e) => e.eventName.includes('assistant'));

      if (firstTopicEvent || firstSessionEvent) {
        firstExperiences.push({
          firstTopicCategory: (firstTopicEvent?.properties.category as string) || 'Unknown',
          firstSessionDuration: firstSessionEvent ? 15 : 0, // Would need session duration tracking
          topicsAddedInFirstSession: userEvents.filter((e) => e.eventName === 'assistant_topic_added').length,
          assistantUsedInFirstSession: assistantEvents.length > 0,
        });
      }
    });

    return {
      funnel,
      activationMetrics: {
        timeToFirstTopic,
        timeToFirstSession,
        onboardingCompletionRate,
      },
      firstExperiences,
      dropOffPoints,
    };
  } catch (error) {
    console.error('Error calculating onboarding analytics:', error);
    throw error;
  }
};

